import th from '@tillhub/javascript-sdk'
import { defineStore } from 'pinia'
import get from 'just-safe-get'
import cloneDeep from 'clone-deep'
import omit from 'just-omit'
import pick from 'just-pick'
import { diff } from 'just-diff'
import compare from 'just-compare'
import {
  calculateAvailableCurrencies,
  disableIdGeneratorsOnEmpty,
  cleanChangeSuggestionData,
  objectSafeSet as set
} from './helpers/configurations'
import { unref, computed } from 'vue'
import { useUiStateStore } from '@shared/store/ui-state'
import { useAuthStore } from '@shared/store/auth'
import { isUnifiedCommerce } from '@shared/constants'
import { checkPermissions } from '@shared/utils/permissions'

export const DEFAULTS = {
  PAGE_SIZES: { main: 20, inline: 5 },
  DEFAULT_CURRENCY: 'EUR',
  DATE_FORMAT: 'dd.MM.yyyy',
  TIME_FORMAT: 'HH:mm',
  TIME_ZONE: 'Europe/Berlin',
  NAVIGATION_AFTER_CREATION: 'list',
  DEFAULT_DATE_SELECTED: 'today'
}

export const useConfigurationStore = defineStore('configuration', {
  state: () => ({
    localConfig: {},
    tenantConfig: {},
    payload: {},
    branchCurrencies: []
  }),

  getters: {
    pageSizes: (state) => {
      const { main, inline } = state.localConfig?.pageSizes || {}
      return {
        main: main || DEFAULTS.PAGE_SIZES.main,
        inline: inline || DEFAULTS.PAGE_SIZES.inline
      }
    },
    locale: (state) => {
      return (
        get(state.localConfig, 'settings.language') ||
        get(state.tenantConfig, 'settings.language')
      )
    },
    defaultCurrency: (state) => {
      return (
        get(state.tenantConfig, 'settings.default_currency') ||
        DEFAULTS.DEFAULT_CURRENCY
      )
    },
    /** @returns {string[]} */
    availableCurrencies(state) {
      return calculateAvailableCurrencies(
        state.branchCurrencies,
        this.defaultCurrency
      )
    },
    dateFormat: (state) => {
      return (
        get(state.tenantConfig, 'settings.default_date_format') ||
        DEFAULTS.DATE_FORMAT
      )
    },
    timeFormat: (state) => {
      return (
        get(state.tenantConfig, 'settings.default_time_format') ||
        DEFAULTS.TIME_FORMAT
      )
    },
    /**  @returns {string} */
    dateTimeFormat() {
      return `${this.dateFormat} ${this.timeFormat}`
    },
    timeZone: (state) => {
      const browserTimezone = () => {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone
        } catch {
          return null
        }
      }
      return (
        get(state.tenantConfig, 'settings.default_timezone') ||
        browserTimezone() ||
        'Europe/Berlin'
      )
    },
    navigationAfterCreation: (state) => {
      return (
        get(state.tenantConfig, 'settings.navigation_after_creation') ||
        DEFAULTS.NAVIGATION_AFTER_CREATION
      )
    },
    tableColumnOrder: (state) => {
      return get(state.tenantConfig, 'settings.tables_columns_order') || null
    },
    // TODO: This can likely be removed. The toggle for the setting has been removed from the settings page.
    tableBorders: (state) => {
      return get(state.tenantConfig, 'settings.general.table_borders') === true
    },
    defaultDateSelected: (state) => {
      return (
        get(state.tenantConfig, 'settings.default_date_selected') ||
        DEFAULTS.DEFAULT_DATE_SELECTED
      )
    },
    customDashboards: (state) => {
      return get(state.tenantConfig, 'custom_dashboards.dashboards') || []
    },
    tenantConfigIsDirty: (state) => {
      return !compare(state.tenantConfig, state.payload)
    },
    hasGlobalDataMode: (state) => {
      return (
        // Expect the value to be 'data_mode' | 'none'
        get(state.tenantConfig, 'settings.debug.global') === 'data_mode'
      )
    }
  },

  actions: {
    $globalReset() {
      // Global reset is called on logout. Maintain the dashboard language setting.
      const locale = get(this.localConfig, 'settings.language')
      this.$reset()
      this.setLocalConfigValue('settings.language', locale)
      this.$persist()
    },
    async loadTenantConfig() {
      try {
        const { data } = await th.configurations().getAll({ owner: 'self' })

        if (!data || !data[0])
          throw new Error(
            'Could not get client account configuration unexpectedly'
          )

        // Assign rather than $patch: the object form of $patch deep-merges, so nested keys
        // of a previously loaded account (e.g. `metadata.fiskaly`) would survive into this one.
        const configuration = data[0]
        this.tenantConfig = configuration
        this.payload = cloneDeep(configuration)
      } catch (err) {
        // Clear both: `payload` is the save baseline, and its `id` is the document a save targets.
        this.tenantConfig = {}
        this.payload = {}
        return [err]
      }
    },
    async loadBranchCurrencies() {
      const authStore = useAuthStore()
      if (
        isUnifiedCommerce() &&
        !checkPermissions(
          { scopes: ['resources:branches:read'] },
          authStore.role,
          authStore.scopes
        )
      ) {
        return
      }

      try {
        const { data = [] } = await th
          .branchesV1()
          .getAll({ query: { deleted: false } })

        this.branchCurrencies = data.map(
          ({ currency_default }) => currency_default
        )
      } catch (err) {
        this.$logException(err, { trackError: false })
      }
    },
    syncTenantConfig() {
      this.payload = cloneDeep(this.tenantConfig)
    },
    resetTenantConfig() {
      this.tenantConfig = cloneDeep(this.payload)
    },
    setLocalConfigValue(path, value) {
      set(this.localConfig, path, cloneDeep(unref(value)))
    },
    setTenantConfigValue(path, value) {
      set(this.tenantConfig, path, cloneDeep(unref(value)))
    },
    setTableColumnOrder(tableName, shownColumns, hiddenColumns) {
      if (!tableName || typeof tableName !== 'string') return
      const path = `tenantConfig.settings.tables_columns_order.${tableName}`
      this.$patch((state) => {
        set(state, `${path}.shown`, cloneDeep(unref(shownColumns)))
        set(state, `${path}.hidden`, cloneDeep(unref(hiddenColumns)))
      })
    },
    checkOnboardingCompleted() {
      // If there are no more onboarding stages to complete
      // Set the property completed to true
      const stages = this.tenantConfig?.onboarding?.stages
      const completed =
        stages &&
        typeof stages === 'object' &&
        Object.values(stages).every(Boolean)

      if (completed) {
        set(this.tenantConfig, 'onboarding.completed', true)
      } else {
        // If it was not completed, then open the onboarding modal
        useUiStateStore().showOnboardList = true
      }
    },
    prepareData() {
      this.$patch((state) => {
        const variableMaximumValue = (
          get(
            state.tenantConfig,
            'discounts.limits.cart_item.variable.maximum_value'
          ) || []
        ).filter((d) => d.currency && d.amount)
        set(
          state.tenantConfig,
          'discounts.limits.cart_item.variable.maximum_value',
          variableMaximumValue
        )

        const fixedMaximumValue = (
          get(
            state.tenantConfig,
            'discounts.limits.cart_item.fixed.maximum_value'
          ) || []
        ).filter((d) => d.currency && d.amount)
        set(
          state.tenantConfig,
          'discounts.limits.cart_item.fixed.maximum_value',
          fixedMaximumValue
        )

        const changeSuggestion = cleanChangeSuggestionData(
          get(state.tenantConfig, 'financials.keep_change_suggestion')
        )
        set(
          state.tenantConfig,
          'financials.keep_change_suggestion',
          changeSuggestion
        )

        const lowCashThreshold = cleanChangeSuggestionData(
          get(state.tenantConfig, 'financials.low_cash_threshold')
        )
        set(
          state.tenantConfig,
          'financials.low_cash_threshold',
          lowCashThreshold
        )
        disableIdGeneratorsOnEmpty(state.tenantConfig)
      })
    },
    async saveTenantConfig() {
      try {
        // since we are still using the PUT route , which can be described as "fake" patch
        // we unfortunately need to "fake" diff the object manually and also exclude some properties that
        // are not writable
        const current = cloneDeep(this.tenantConfig)
        const changedProps = diff(this.payload, current).map(
          (item) => item.path[0]
        )

        const payload = omit(pick(current, changedProps), [
          'id',
          'updated_at',
          'created_at',
          'metadata'
        ])

        const { data } = await th.configurations().put(this.payload.id, payload)

        if (!data) {
          throw new Error(
            'Could not get client account configuration unexpectedly'
          )
        }

        // Assign rather than $patch, for the same reason as in loadTenantConfig().
        this.tenantConfig = data
        this.payload = cloneDeep(data)

        return [null]
      } catch (err) {
        return [err]
      }
    }
  },

  // Persist this store to localStorage (via pinia-plugin-persistedstate plugin)
  persist: {
    // Persist local user preferences only to ensure we don't get localStorage QuotaExceededError (see UNTIL-18230).
    pick: ['localConfig']
  }
})

/**
 * Creates a writable computed ref that maps a target path to a tenant configuration value from the configuration store
 * @param {string | string[]} target Target path as dot-separated string or array of strings
 * @param {any} [defaultValue] Optional fallback value
 * @returns {WritableComputedRef<any, any>}
 */
export const useWritableTenantConfigValue = (target, defaultValue) => {
  const store = useConfigurationStore()
  return computed({
    get: () => get(store.tenantConfig, target, defaultValue),
    set: (value) => {
      store.setTenantConfigValue(target, value)
    }
  })
}

/**
 * Creates a writable computed ref that maps a target path to a local configuration value from the configuration store
 * @param {string | string[]} target Target path as dot-separated string or array of strings
 * @param {any} [defaultValue] Optional fallback value
 * @returns {WritableComputedRef<any, any>}
 */
export const useWritableLocalConfigValue = (target, defaultValue) => {
  const store = useConfigurationStore()
  return computed({
    get: () => get(store.localConfig, target, defaultValue),
    set: (value) => {
      store.setLocalConfigValue(target, value)
    }
  })
}
